import { Grid3x3, Heart, LogOut, Search, ShoppingCart } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/auth-context'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/logo'
import { useCategories } from '@/features/categories/api'
import { useCart } from '@/features/cart/api'
import { dashboardPathForRole } from '@/lib/routes'
import { cn } from '@/lib/cn'

const UTILITY_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/courses', label: 'Recorded Courses' },
  { to: '/blog', label: 'Blog' },
  { to: '/instructors', label: 'Our Instructors' },
]

export function SiteHeader() {
  const { user, status, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [coursesMenuOpen, setCoursesMenuOpen] = useState(false)
  const { data: categories } = useCategories()
  const cart = useCart({ enabled: user?.role === 'learner' })

  async function handleLogout() {
    setMenuOpen(false)
    await logout()
    navigate('/')
  }

  return (
    <header className="sticky top-0 z-30">
      {/* Utility bar: primary nav + auth links, always visible above the main row. */}
      <div className="bg-ink">
        <div className="mx-auto flex h-11 w-full max-w-[1320px] items-center justify-between px-4 sm:px-6 lg:px-10">
          <nav className="hidden items-center gap-6 text-sm font-medium text-paper/75 md:flex">
            {UTILITY_LINKS.map((link) => {
              const isActive =
                link.to === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(link.to)
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={cn(
                    'relative flex h-11 items-center border-t-2 border-transparent transition-colors hover:text-paper',
                    isActive && 'border-accent text-paper',
                  )}
                >
                  {link.label}
                </Link>
              )
            })}
          </nav>
          <div className="flex items-center gap-4 text-sm text-paper/75">
            {status !== 'loading' && !user && (
              <>
                <Link to="/login" className="hover:text-paper">
                  Login
                </Link>
                <span className="text-paper/30">|</span>
                <Link to="/register" className="hover:text-paper">
                  Register
                </Link>
              </>
            )}
            <Link to="/physical-classes">
              <Button size="sm">Send Enquiry</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main row: logo, category menu, search, cart/wishlist, primary CTA. */}
      <div className="border-b border-line bg-paper-raised">
        <div className="mx-auto flex h-20 w-full max-w-[1320px] items-center gap-6 px-4 sm:px-6 lg:px-10">
          <Link to="/" aria-label="Become A Doer home">
            <Logo />
          </Link>

          <div
            className="relative hidden shrink-0 md:block"
            onMouseEnter={() => setCoursesMenuOpen(true)}
            onMouseLeave={() => setCoursesMenuOpen(false)}
          >
            <Link
              to="/courses"
              className="flex items-center gap-2 rounded-md border border-line-strong px-3 py-2 text-sm font-medium text-ink hover:border-ink"
            >
              <Grid3x3 className="size-4" aria-hidden />
              Courses
            </Link>
            {coursesMenuOpen && categories && categories.length > 0 && (
              <div className="absolute left-0 top-full w-64 rounded-md border border-line bg-paper-raised py-1 shadow-raised">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/courses?category=${category.id}`}
                    className="block px-3 py-2 text-sm text-ink-muted hover:bg-paper hover:text-ink"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="hidden flex-1 items-center md:flex">
            <Link
              to="/courses"
              className="flex w-full items-center gap-2 rounded-md border border-line-strong bg-paper px-3 py-2.5 text-sm text-ink-faint transition-colors hover:border-ink-faint"
            >
              <Search className="size-4" aria-hidden />
              What skill do you want to learn?
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <Link
              to="/wishlist"
              title="Wishlist"
              className="hidden text-ink-faint transition-colors hover:text-ink sm:block"
            >
              <Heart className="size-5" aria-hidden />
            </Link>
            {user?.role === 'learner' ? (
              <Link
                to="/cart"
                title="Cart"
                className="relative hidden text-ink-faint transition-colors hover:text-ink sm:block"
              >
                <ShoppingCart className="size-5" aria-hidden />
                {cart.data && cart.data.length > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full bg-accent text-[10px] font-semibold text-ink">
                    {cart.data.length}
                  </span>
                )}
              </Link>
            ) : (
              <button
                type="button"
                title="Cart"
                disabled
                className="hidden text-ink-faint sm:block"
              >
                <ShoppingCart className="size-5" aria-hidden />
              </button>
            )}

            {status === 'loading' ? null : user ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((open) => !open)}
                  className="flex size-9 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-ink"
                >
                  {user.email.slice(0, 1).toUpperCase()}
                </button>
                {menuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-56 rounded-md border border-line bg-paper-raised py-1 shadow-raised"
                    onMouseLeave={() => setMenuOpen(false)}
                  >
                    <div className="border-b border-line px-3 py-2">
                      <p className="truncate text-sm font-medium text-ink">
                        {user.email}
                      </p>
                      <p className="text-xs capitalize text-ink-muted">
                        {user.role}
                      </p>
                    </div>
                    <Link
                      to={dashboardPathForRole(user.role)}
                      className="block px-3 py-2 text-sm text-ink hover:bg-paper"
                      onClick={() => setMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/profile"
                      className="block px-3 py-2 text-sm text-ink hover:bg-paper"
                      onClick={() => setMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-paper"
                    >
                      <LogOut className="size-3.5" aria-hidden />
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/physical-classes">
                <Button>Physical Classes</Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
